import { Shell } from "@/components/layout/Shell";
import { useEmpresas } from "@/hooks/use-data";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Building2,
  MapPin,
  Mail,
  Phone,
  Plus,
  X,
  Loader2,
  Upload,
  Users,
  CalendarFold,
  Search,
} from "lucide-react";
import { useState, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { LogoAvatar } from "@/components/LogoUpload";
import { api } from "@shared/routes";
import { ImportarParceirosDialog } from "@/components/empresas/ImportarParceirosDialog";
import {
  fmtDateTime,
  formatCnpj,
  formatCpf,
  formatPhone,
} from "@/lib/formatters";
import { UF_LIST } from "@/lib/constants";

function CreateEmpresaForm({
  tipo,
  vistoriadoras: vistoriadoras,
  onClose,
  isOwnerCreating = false,
}: {
  tipo: "VISTORIADORA" | "PARCEIRO_DE_NEGOCIOS";
  vistoriadoras: { id: string; razaoSocial: string }[];
  onClose: () => void;
  isOwnerCreating?: boolean;
}) {
  const { session } = useAuth();
  const { toast } = useToast();
  const [razaoSocial, setRazaoSocial] = useState("");
  const [nomeFantasia, setNomeFantasia] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [uf, setUf] = useState("");
  const [telefone, setTelefone] = useState("");
  const [vistoriadoraId, setVistoriadoraId] = useState("");
  const [cidade, setCidade] = useState("");
  const [tagEmpresa, setTagEmpresa] = useState("");
  const [idProprio, setIdProprio] = useState("");

  const mutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await fetch("/api/empresas", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao criar empresa");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.empresas.list.path] });
      toast({
        title: "Empresa criada",
        description: `${tipo === "VISTORIADORA" ? "Vistoriadora" : "Solicitante"} cadastrado(a) com sucesso.`,
      });
      onClose();
    },
    onError: (err: Error) => {
      toast({
        title: "Erro",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!razaoSocial || !uf || !cnpj || !cidade) return;

    // Para vistoriadora, tagEmpresa é obrigatória
    if (tipo === "VISTORIADORA" && !tagEmpresa) {
      toast({
        title: "Erro",
        description: "Tag é obrigatória para vistoriadora",
        variant: "destructive",
      });
      return;
    }

    const data: any = {
      tipo,
      razaoSocial,
      nomeFantasia: nomeFantasia || undefined,
      cnpj: cnpj.replace(/\D/g, "") || undefined,
      uf,
      telefone: telefone || undefined,
      cidade,
    };

    if (
      (tipo === "VISTORIADORA" || tipo === "PARCEIRO_DE_NEGOCIOS") &&
      tagEmpresa
    ) {
      data.tagEmpresa = tagEmpresa.toUpperCase();
    }

    if (tipo === "PARCEIRO_DE_NEGOCIOS" && vistoriadoraId) {
      data.vistoriadoraId = vistoriadoraId;
    }

    if (tipo === "PARCEIRO_DE_NEGOCIOS" && idProprio) {
      const parsed = parseInt(idProprio, 10);
      if (!isNaN(parsed)) data.idProprio = parsed;
    }

    mutation.mutate(data);
  };

  return (
    <Card className="border-2 border-primary/20 shadow-lg">
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <CardTitle className="text-base">
          Nova {tipo === "VISTORIADORA" ? "Vistoriadora" : "Solicitante"}
        </CardTitle>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          data-testid="button-close-form"
        >
          <X className="w-4 h-4" />
        </Button>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Razão Social *</Label>
              <Input
                value={razaoSocial}
                onChange={(e) => setRazaoSocial(e.target.value)}
                required
                data-testid="input-empresa-razao"
              />
            </div>
            <div className="space-y-2">
              <Label>Nome Fantasia</Label>
              <Input
                value={nomeFantasia}
                onChange={(e) => setNomeFantasia(e.target.value)}
                data-testid="input-empresa-fantasia"
              />
            </div>
            <div className="space-y-2">
              <Label>CNPJ *</Label>
              <Input
                value={cnpj}
                onChange={(e) => setCnpj(formatCnpj(e.target.value))}
                placeholder="00.000.000/0000-00"
                required
                data-testid="input-empresa-cnpj"
              />
            </div>
            <div className="space-y-2">
              <Label>UF *</Label>
              <Select value={uf} onValueChange={setUf}>
                <SelectTrigger data-testid="select-empresa-uf">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {UF_LIST.map((u) => (
                    <SelectItem key={u} value={u}>
                      {u}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Telefone</Label>
              <Input
                value={telefone}
                onChange={(e) => setTelefone(formatPhone(e.target.value))}
                placeholder="(00) 00000-0000"
                maxLength={15}
                inputMode="numeric"
                data-testid="input-empresa-telefone"
              />
            </div>
            <div className="space-y-2">
              <Label>Cidade *</Label>
              <Input
                value={cidade}
                onChange={(e) => setCidade(e.target.value)}
                required
                data-testid="input-empresa-cidade"
              />
            </div>
            {tipo === "PARCEIRO_DE_NEGOCIOS" && (
              <div className="space-y-2">
                <Label>ID Próprio</Label>
                <Input
                  type="number"
                  value={idProprio}
                  onChange={(e) => setIdProprio(e.target.value)}
                  placeholder="Código numérico interno (opcional)"
                  data-testid="input-empresa-id-proprio"
                />
                <p className="text-xs text-muted-foreground">
                  Identificador numérico próprio da vistoriadora para este
                  parceiro. Facilita a identificação quando há nomes similares.
                </p>
              </div>
            )}
            {tipo === "PARCEIRO_DE_NEGOCIOS" && (
              <div className="space-y-2 md:col-span-2">
                <div>
                  <Label>Tag para Identificação de Laudos</Label>
                  <p className="text-xs text-muted-foreground mt-1 mb-2">
                    Opcional. Se definida, os laudos de avaliação deste
                    solicitante ficam: RM-LA-{"{TAG}"}-0001. Sem tag, o padrão
                    continua sendo RM-LA-0001.
                  </p>
                </div>
                <Input
                  value={tagEmpresa}
                  onChange={(e) => setTagEmpresa(e.target.value.toUpperCase())}
                  placeholder="Ex: REM(RM-LA-REM-0001)"
                  maxLength={10}
                  data-testid="input-empresa-tag-solicitante"
                />
              </div>
            )}
            {tipo === "VISTORIADORA" && (
              <div className="space-y-2 md:col-span-2">
                <div>
                  <Label>Tag para Identificação de Laudos *</Label>
                  <p className="text-xs text-muted-foreground mt-1 mb-2">
                    Código único usado para identificar os laudos. Ex: Se sua
                    tag é <strong>REM</strong>, os laudos serão: RM-LA-REM-0001,
                    RM-LA-REM-0002, etc.
                  </p>
                </div>
                <Input
                  value={tagEmpresa}
                  onChange={(e) => setTagEmpresa(e.target.value.toUpperCase())}
                  placeholder="Ex: REM"
                  maxLength={10}
                  data-testid="input-empresa-tag"
                  required
                />
              </div>
            )}
            {tipo === "PARCEIRO_DE_NEGOCIOS" &&
              isOwnerCreating &&
              vistoriadoras.length > 0 && (
                <div className="space-y-2 md:col-span-2">
                  <Label>Vinculada à Vistoriadora *</Label>
                  <Select
                    value={vistoriadoraId}
                    onValueChange={setVistoriadoraId}
                  >
                    <SelectTrigger data-testid="select-empresa-vistoriadora">
                      <SelectValue placeholder="Selecione a vistoriadora" />
                    </SelectTrigger>
                    <SelectContent>
                      {vistoriadoras.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.razaoSocial}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending}
              data-testid="button-submit-empresa"
            >
              {mutation.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Cadastrar
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export default function Empresas() {
  const { data: empresas, isLoading } = useEmpresas();
  const { profile, session } = useAuth();
  const isOwner = profile?.roles?.includes("owner");
  const isVistoriadora = profile?.roles?.some((r: string) =>
    ["admin", "rt"].includes(r),
  );

  const [showFormVistoriadora, setShowFormVistoriadora] = useState(false);
  const [showFormParceiro, setShowFormParceiro] = useState(false);
  const [showImportarParceiros, setShowImportarParceiros] = useState(false);
  const [filterClienteId, setFilterClienteId] = useState<string>("all");
  const [searchVistoriadora, setSearchVistoriadora] = useState("");
  const [searchParceiro, setSearchParceiro] = useState("");
  const [filterUF, setFilterUF] = useState("all");

  const { toast } = useToast();

  const vistoriadoras =
    empresas?.filter((e) => e.tipo === "VISTORIADORA") || [];
  const parceiros =
    empresas?.filter((e) => e.tipo === "PARCEIRO_DE_NEGOCIOS") || [];
  const minhaEmpresa = empresas?.find((e) => e.id === profile?.empresaId);

  const vistoriadorasFiltradas = useMemo(() => {
    let list =
      filterClienteId === "all"
        ? vistoriadoras
        : vistoriadoras.filter((v) => {
            const clienteSelecionado = parceiros.find(
              (p) => p.id === filterClienteId,
            );
            return clienteSelecionado?.vistoriadoraId === v.id;
          });
    if (searchVistoriadora.trim()) {
      const q = searchVistoriadora.toLowerCase();
      list = list.filter(
        (v) =>
          v.razaoSocial?.toLowerCase().includes(q) ||
          v.nomeFantasia?.toLowerCase().includes(q) ||
          v.tagEmpresa?.toLowerCase().includes(q) ||
          v.cidade?.toLowerCase().includes(q),
      );
    }
    return list;
  }, [filterClienteId, vistoriadoras, parceiros, searchVistoriadora]);

  const parceirosFiltrados = useMemo(() => {
    let list = [...parceiros].sort((a, b) =>
      b.criadoEm > a.criadoEm ? 1 : -1,
    );
    if (searchParceiro.trim()) {
      const q = searchParceiro.toLowerCase();
      list = list.filter(
        (p) =>
          p.razaoSocial?.toLowerCase().includes(q) ||
          p.nomeFantasia?.toLowerCase().includes(q) ||
          p.cidade?.toLowerCase().includes(q) ||
          p.cnpj?.includes(q) ||
          p.cpf?.includes(q),
      );
    }
    if (filterUF !== "all") {
      list = list.filter((p) => p.uf === filterUF);
    }
    return list;
  }, [parceiros, searchParceiro, filterUF]);

  const pageTitle = isOwner ? "Empresas" : "Solicitantes";
  const pageSubtitle = isOwner
    ? "Gerencie empresas vistoriadoras e solicitantes"
    : "Gerencie os parceiros vinculados à sua empresa";

  return (
    <Shell>
      <div className="mb-8">
        <h1
          className="text-3xl font-bold font-display text-slate-900"
          data-testid="text-page-title"
        >
          {pageTitle}
        </h1>
        <p className="text-muted-foreground mt-1">{pageSubtitle}</p>
      </div>

      {isOwner && (
        <div className="mb-6">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <h2 className="text-lg font-semibold text-slate-800 mr-auto">
              Vistoriadoras ({vistoriadorasFiltradas.length}
              {filterClienteId !== "all" ? ` de ${vistoriadoras.length}` : ""})
            </h2>
            {parceiros.length > 0 && (
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-slate-400" />
                <Select
                  value={filterClienteId}
                  onValueChange={setFilterClienteId}
                >
                  <SelectTrigger
                    className="w-[220px]"
                    data-testid="select-filter-cliente"
                  >
                    <SelectValue placeholder="Todos os clientes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os clientes</SelectItem>
                    {parceiros.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.nomeFantasia || p.razaoSocial}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {filterClienteId !== "all" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFilterClienteId("all")}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            )}
            {!showFormVistoriadora && (
              <Button
                size="sm"
                onClick={() => setShowFormVistoriadora(true)}
                data-testid="button-add-vistoriadora"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nova Vistoriadora
              </Button>
            )}
          </div>

          {showFormVistoriadora && (
            <div className="mb-4">
              <CreateEmpresaForm
                tipo="VISTORIADORA"
                vistoriadoras={[]}
                onClose={() => setShowFormVistoriadora(false)}
              />
            </div>
          )}

          <div className="flex items-center gap-2 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar vistoriadora..."
                value={searchVistoriadora}
                onChange={(e) => setSearchVistoriadora(e.target.value)}
                className="pl-8 h-9"
              />
            </div>
            {searchVistoriadora && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearchVistoriadora("")}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {isLoading ? (
              Array(3)
                .fill(0)
                .map((_, i) => <Skeleton key={i} className="h-48 rounded-xl" />)
            ) : vistoriadorasFiltradas.length > 0 ? (
              vistoriadorasFiltradas.map((empresa) => (
                <Card
                  key={empresa.id}
                  className="border-none shadow-md hover:shadow-lg transition-shadow"
                  data-testid={`card-empresa-${empresa.id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <LogoAvatar
                          empresaId={empresa.id}
                          logoUrl={empresa.logoUrl}
                          fallback={
                            empresa.razaoSocial
                              ?.substring(0, 2)
                              .toUpperCase() || "E"
                          }
                          size="sm"
                        />
                        <div>
                          <CardTitle className="text-base">
                            {empresa.razaoSocial}
                          </CardTitle>
                          {empresa.nomeFantasia && (
                            <p className="text-xs text-muted-foreground">
                              {empresa.nomeFantasia}
                            </p>
                          )}
                        </div>
                      </div>
                      <Badge
                        variant="secondary"
                        className="bg-blue-100 text-blue-700"
                      >
                        Vistoriadora
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    {empresa.cnpj && (
                      <p className="text-slate-600">
                        CNPJ: {formatCnpj(empresa.cnpj)}
                      </p>
                    )}
                    {empresa.tagEmpresa && (
                      <div className="mt-2 pt-2 border-t">
                        <p className="text-xs text-muted-foreground mb-1">
                          Tag para Laudos
                        </p>
                        <p className="font-mono font-bold text-primary">
                          {empresa.tagEmpresa}
                        </p>
                      </div>
                    )}
                    {empresa.email && (
                      <div className="flex items-center gap-2 text-slate-500">
                        <Mail className="w-3.5 h-3.5" />
                        <span className="truncate">{empresa.email}</span>
                      </div>
                    )}
                    {empresa.telefone && (
                      <div className="flex items-center gap-2 text-slate-500">
                        <Phone className="w-3.5 h-3.5" />
                        <span>{empresa.telefone}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-slate-500">
                      <MapPin className="w-3.5 h-3.5" />
                      <span>
                        {empresa.cidade ? `${empresa.cidade}, ` : ""}
                        {empresa.uf}
                      </span>
                    </div>
                    {empresa.codigoConvite && (
                      <div className="mt-2 pt-2 border-t">
                        <p className="text-xs text-muted-foreground">
                          Código de Convite
                        </p>
                        <p className="font-mono font-bold text-primary">
                          {empresa.codigoConvite}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="col-span-full text-center py-12 text-muted-foreground">
                {filterClienteId !== "all"
                  ? "Nenhuma vistoriadora encontrada para este cliente"
                  : "Nenhuma empresa vistoriadora encontrada"}
              </div>
            )}
          </div>
        </div>
      )}

      {(isOwner || isVistoriadora) && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-800">
              {isOwner
                ? `Solicitantes (${parceiros.length})`
                : `Meus Parceiros (${parceiros.length})`}
            </h2>
            <div className="flex items-center gap-2">
              {isVistoriadora && !isOwner && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowImportarParceiros(true)}
                  data-testid="button-import-parceiros"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Importar CSV/XLSX
                </Button>
              )}
              {(isOwner || isVistoriadora) && !showFormParceiro && (
                <Button
                  size="sm"
                  onClick={() => setShowFormParceiro(true)}
                  data-testid="button-add-parceiro"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Parceiro
                </Button>
              )}
            </div>
          </div>

          {showFormParceiro && (
            <div className="mb-4">
              <CreateEmpresaForm
                tipo="PARCEIRO_DE_NEGOCIOS"
                vistoriadoras={
                  isOwner
                    ? vistoriadoras.map((c) => ({
                        id: c.id,
                        razaoSocial: c.razaoSocial,
                      }))
                    : []
                }
                isOwnerCreating={!!isOwner}
                onClose={() => setShowFormParceiro(false)}
              />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar parceiro..."
                value={searchParceiro}
                onChange={(e) => setSearchParceiro(e.target.value)}
                className="pl-8 h-9"
              />
            </div>
            <Select value={filterUF} onValueChange={setFilterUF}>
              <SelectTrigger className="w-[100px] h-9">
                <SelectValue placeholder="UF" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {UF_LIST.map((uf) => (
                  <SelectItem key={uf} value={uf}>
                    {uf}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(searchParceiro || filterUF !== "all") && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchParceiro("");
                  setFilterUF("all");
                }}
              >
                <X className="w-4 h-4 mr-1" />
                Limpar
              </Button>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {isLoading ? (
              Array(3)
                .fill(0)
                .map((_, i) => <Skeleton key={i} className="h-40 rounded-xl" />)
            ) : parceirosFiltrados.length > 0 ? (
              parceirosFiltrados.map((empresa) => (
                <Card
                  key={empresa.id}
                  className="border-none shadow-md hover:shadow-lg transition-shadow"
                  data-testid={`card-empresa-${empresa.id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <LogoAvatar
                          empresaId={empresa.id}
                          logoUrl={empresa.logoUrl}
                          fallback={
                            empresa.razaoSocial
                              ?.substring(0, 2)
                              .toUpperCase() || "E"
                          }
                          size="sm"
                        />
                        <div>
                          <CardTitle className="text-base">
                            {empresa.razaoSocial}
                            {(empresa as any).idProprio != null && (
                              <span className="ml-1.5 text-sm font-normal text-muted-foreground">
                                (#{(empresa as any).idProprio})
                              </span>
                            )}
                          </CardTitle>
                          {empresa.nomeFantasia && (
                            <p className="text-xs text-muted-foreground">
                              {empresa.nomeFantasia}
                            </p>
                          )}
                        </div>
                      </div>
                      <Badge
                        variant="secondary"
                        className="bg-orange-100 text-orange-700"
                      >
                        Parceiro
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    {(empresa.cnpj || empresa.cpf) && (
                      <p className="text-slate-600">
                        {empresa.cnpj
                          ? `CNPJ: ${formatCnpj(empresa.cnpj)}`
                          : `CPF: ${formatCpf(empresa.cpf ?? "")}`}
                      </p>
                    )}
                    {empresa.email && (
                      <div className="flex items-center gap-2 text-slate-500">
                        <Mail className="w-3.5 h-3.5" />
                        <span className="truncate">{empresa.email}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-slate-500">
                      <MapPin className="w-3.5 h-3.5" />
                      <span>
                        {empresa.cidade ? `${empresa.cidade}, ` : ""}
                        {empresa.uf}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-slate-500">
                      <CalendarFold className="w-3.5 h-3.5" />
                      <span>
                        {new Date(empresa.criadoEm).toLocaleDateString("pt-BR")}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="col-span-full text-center py-12 text-muted-foreground">
                Nenhum solicitante encontrado
              </div>
            )}
          </div>
        </div>
      )}

      <ImportarParceirosDialog
        open={showImportarParceiros}
        onClose={() => setShowImportarParceiros(false)}
      />

      {!isOwner && !isVistoriadora && minhaEmpresa && (
        <div>
          <h2 className="text-lg font-semibold text-slate-800 mb-4">
            Minha Empresa
          </h2>
          <Card
            className="border-none shadow-md max-w-md"
            data-testid={`card-empresa-${minhaEmpresa.id}`}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <LogoAvatar
                  empresaId={minhaEmpresa.id}
                  logoUrl={minhaEmpresa.logoUrl}
                  fallback={
                    minhaEmpresa.razaoSocial?.substring(0, 2).toUpperCase() ||
                    "E"
                  }
                  size="sm"
                />
                <div>
                  <CardTitle className="text-base">
                    {minhaEmpresa.razaoSocial}
                  </CardTitle>
                  {minhaEmpresa.nomeFantasia && (
                    <p className="text-xs text-muted-foreground">
                      {minhaEmpresa.nomeFantasia}
                    </p>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {minhaEmpresa.email && (
                <div className="flex items-center gap-2 text-slate-500">
                  <Mail className="w-3.5 h-3.5" />
                  <span className="truncate">{minhaEmpresa.email}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-slate-500">
                <MapPin className="w-3.5 h-3.5" />
                <span>
                  {minhaEmpresa.cidade ? `${minhaEmpresa.cidade}, ` : ""}
                  {minhaEmpresa.uf}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Shell>
  );
}
